import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Outlet } from 'react-router-dom';
import { apiFetch } from '../api';
import { useAuth } from '../context/AuthContext';
import { useTenantData } from '../context/TenantDataContext';
import Sidebar from '../components/Sidebar';
import { SideMenuContext } from '../components/SideMenu';
import { LicenseExpired } from '../components/LicenseGate';
import './styles.css';
import LoadingState from '../components/LoadingState';

// TenantDataProvider/NotesDrawerProvider are mounted in
// App.jsx, not here — an object-detail overlay drawer renders as a second,
// sibling Routes tree outside this component (see App.jsx), and it needs
// the exact same context instance as the list page underneath it, not a
// second one of its own.
export default function TenantLayout() {
  const { tenantId } = useParams();
  return <TenantLayoutInner tenantId={tenantId} />;
}

function TenantLayoutInner({ tenantId }) {
  const { role } = useAuth();
  const { tenant, loading, errors } = useTenantData();
  const [allTenants, setAllTenants] = useState([]);
  // The second, vertical menu (SideMenu) renders into a slot between the sidebar and the page; `menuCount` is how
  // many are open right now, and the main sidebar folds in while there is one.
  const [menuSlot, setMenuSlot] = useState(null);
  const [menuCount, setMenuCount] = useState(0);
  const menuCtx = useMemo(() => ({ slot: menuSlot, setMenuCount }), [menuSlot]);

  useEffect(() => {
    if (role !== 'admin') return;
    apiFetch('/api/tenants')
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) setAllTenants(data);
      })
      .catch(() => {});
  }, [role]);

  // The sidebar/shell itself always renders, loading or not — swapping the
  // ENTIRE shell out for a full-page loading state (as this used to do)
  // meant the sidebar visibly vanished and reappeared on every tenant page
  // that didn't already have its data cached, which read as the whole app
  // reloading rather than a normal in-app navigation. Sidebar already
  // handles `tenant` being null (optional-chained throughout), so it's
  // safe to keep mounted while this is still loading.
  // An expired Trial keeps the shell on screen, greyed out and inert, under a dialog (LicenseExpired).
  // The dialog is a sibling of .nc-main, not inside it: the grey-out uses `filter`, which would
  // otherwise also grey (and pin to) the fixed-position dialog.
  const expired = !loading && !!tenant?.license?.expired;
  return (
    <SideMenuContext.Provider value={menuCtx}>
      <div className={`nc-shell${expired ? ' nc-shell--expired' : ''}`}>
        <Sidebar tenantId={tenantId} allTenants={allTenants} narrow={menuCount > 0} />
        <div className="nc-submenu-slot" ref={setMenuSlot} />
        <div className="nc-main">
          <div className="nc-content">
            {loading ? (
              <LoadingState />
            ) : errors.tenant || !tenant ? (
              <div className="state-error">Tenant ikke fundet</div>
            ) : expired ? null : (
              <Outlet />
            )}
          </div>
        </div>
        {expired && <LicenseExpired />}
      </div>
    </SideMenuContext.Provider>
  );
}
